import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLeadForm } from '../../../contexts/LeadFormContext';

const WishlistPanel = ({ products, isOpen, onClose, onRemoveProduct, onClearAll, onAddToComparison, comparisonItems }) => {
  const { openLeadForm } = useLeadForm();
  
  if (!isOpen) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(price);
  };

  const handleWhatsAppContact = (product) => {
    openLeadForm();
  };

  const handleWhatsAppContactAll = () => {
    openLeadForm();
  };

  const EmptyWishlist = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-6">
        <Icon name="Heart" size={32} className="text-text-secondary" />
      </div>
      <h3 className="font-playfair font-semibold text-xl text-text-primary mb-2">
        Sua lista de desejos está vazia
      </h3>
      <p className="text-text-secondary mb-6 max-w-md">
        Adicione produtos que você gosta para acompanhar e comparar facilmente.
      </p>
      <Button 
        variant="outline" 
        iconName="ArrowLeft" 
        iconPosition="left"
        onClick={onClose}
      >
        Explorar Catálogo
      </Button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-premium-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="font-playfair font-bold text-2xl text-text-primary">
              Lista de Desejos
            </h2>
            <p className="text-text-secondary mt-1">
              {products?.length} {products?.length === 1 ? 'produto salvo' : 'produtos salvos'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {products?.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onClearAll}
                  iconName="Trash2"
                  iconPosition="left"
                >
                  Limpar Tudo
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleWhatsAppContactAll}
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="bg-gradient-forest"
                >
                  Consultar Todos
                </Button>
              </>
            )}
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-muted hover:bg-border flex items-center justify-center transition-colors duration-200"
            >
              <Icon name="X" size={20} className="text-text-primary" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-auto max-h-[calc(90vh-140px)]">
          {products?.length === 0 ? (
            <EmptyWishlist />
          ) : (
            <div className="p-6">
              <div className="space-y-4">
                {products?.map((product) => (
                  <div key={product?.id} className="bg-muted rounded-xl p-4 flex gap-4">
                    {/* Product Image */}
                    <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-background">
                      <Image
                        src={product?.images?.[0] || product?.image}
                        alt={product?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-1">
                            {product?.category}
                          </div>
                          <h3 className="font-playfair font-semibold text-lg text-text-primary line-clamp-1">
                            {product?.name}
                          </h3>
                        </div>
                        <button
                          onClick={() => onRemoveProduct(product?.id)}
                          className="w-8 h-8 rounded-full bg-background hover:bg-red-50 flex items-center justify-center transition-colors duration-200 group"
                        >
                          <Icon name="Heart" size={16} className="text-red-500 group-hover:text-red-600" fill="currentColor" />
                        </button>
                      </div>

                      <p className="text-sm text-text-secondary mb-3 line-clamp-2">
                        {product?.description}
                      </p>

                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-bold text-lg text-text-primary">
                            {product?.priceRange ? 
                              `${formatPrice(product?.priceRange?.min)} - ${formatPrice(product?.priceRange?.max)}` :
                              formatPrice(product?.price)
                            }
                          </div>
                          <div className="text-xs text-text-secondary">
                            {product?.woodType} • {product?.dimensions}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onAddToComparison(product?.id)}
                            disabled={comparisonItems?.includes(product?.id) || comparisonItems?.length >= 3}
                            iconName="GitCompare"
                            iconPosition="left"
                          >
                            {comparisonItems?.includes(product?.id) ? 'Na Comparação' : 'Comparar'}
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleWhatsAppContact(product)}
                            iconName="MessageCircle"
                            iconPosition="left"
                            className="bg-gradient-forest"
                          >
                            Consultar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Additional Info */}
              {products?.length > 0 && (
                <div className="mt-6 p-4 bg-background rounded-lg border border-border">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={20} className="text-primary mt-0.5" />
                    <div>
                      <h4 className="font-medium text-text-primary mb-1">
                        Dica: Maximize sua experiência
                      </h4>
                      <p className="text-sm text-text-secondary">
                        Use a função de comparação para analisar até 3 produtos lado a lado. 
                        Entre em contato via WhatsApp para receber orientação personalizada sobre 
                        customização e prazos de entrega.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {products?.length > 0 && (
          <div className="border-t border-border p-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Continuar Navegando
              </Button>
              <Button
                variant="default"
                iconName="MessageCircle"
                iconPosition="left"
                onClick={handleWhatsAppContactAll}
                className="flex-1 bg-gradient-forest"
              >
                Consultar Lista Completa
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPanel;